/**
 * Text utility functions for consistent text transformations across the app.
 * Single source of truth for text formatting operations.
 */

/**
 * Get initials from a name string.
 *
 * @param name - The full name to extract initials from
 * @returns Two-character initials in uppercase
 *
 * @example
 * getInitials("Christian Skaiaa") // "CS"
 * getInitials("Christian")        // "CH"
 * getInitials("")                 // "?"
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "?";
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Format a display name with optional text transformation.
 *
 * @param name - The name to format
 * @param transform - Optional transformation: 'uppercase', 'capitalize', or 'none'
 * @returns The formatted name
 *
 * @example
 * formatDisplayName("christian skaiaa", "uppercase")  // "CHRISTIAN SKAIAA"
 * formatDisplayName("christian skaiaa", "capitalize") // "Christian Skaiaa"
 * formatDisplayName("CHRISTIAN SKAIAA", "none")       // "CHRISTIAN SKAIAA"
 */
export function formatDisplayName(
  name: string,
  transform: "uppercase" | "capitalize" | "none" = "none",
): string {
  switch (transform) {
    case "uppercase":
      return name.toUpperCase();
    case "capitalize":
      return name
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    case "none":
    default:
      return name;
  }
}
